import { Autocomplete, InputLabel, Stack, TextField } from '@mui/material'
import { Controller, useFormContext } from 'react-hook-form'
import FormSingleColumnLayout from 'layouts/form-single-column-layout/form-single-column-layout'
import TextInputField from 'components/fields/text-input-field/text-input-field.component'
import { LoadConfirmFormContainer } from 'components/forms/forms.styles'
import { CarrierCompany } from 'store/company/company.types'
import {
  useGetCompanyByIdQuery,
  useGetCompanyEmailsQuery,
  useGetCompanyPhonesQuery,
} from 'store/company/company.api'
import { useEffect, useRef } from 'react'
import {
  findDispatchEmail,
  findDispatchPhone,
  formatCompanyAddress,
} from 'pages/address/address-details/carrier-details/carrier-contact.utils'
import { formatPhoneForDisplay } from 'utils/phone'

type CarrierSuggestion = {
  id: string
  companyName: string
  companyId: string
  dispatchName: string
}

type LoadCarrierFormProps = {
  carrierSuggestions: CarrierSuggestion[]
  carrierSuggestionsLoading?: boolean
  skipAutoFill?: boolean
  compact?: boolean
}

const CarrierAutoFill = ({ companyId }: { companyId: string }) => {
  const { getValues, setValue } = useFormContext()
  const { data: company } = useGetCompanyByIdQuery({ id: companyId })
  const { data: emails = [] } = useGetCompanyEmailsQuery({ companyId })
  const { data: phones = [] } = useGetCompanyPhonesQuery({ companyId })
  const autofillKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!company) return

    const autofillKey = `${companyId}:${emails.length}:${phones.length}`
    if (autofillKeyRef.current === autofillKey) return
    autofillKeyRef.current = autofillKey

    const current = getValues()
    if (!current.carrierAddress?.trim()) {
      setValue('carrierAddress', formatCompanyAddress(company), { shouldDirty: true })
    }
    if (!current.dispatchEmail?.trim()) {
      setValue('dispatchEmail', findDispatchEmail(emails)?.email ?? '', {
        shouldDirty: true,
      })
    }
    if (!current.dispatchPhone?.trim()) {
      setValue(
        'dispatchPhone',
        formatPhoneForDisplay(findDispatchPhone(phones, 'Dispatch')?.phone),
        { shouldDirty: true }
      )
    }
    if (!current.dispatchCell?.trim()) {
      setValue(
        'dispatchCell',
        formatPhoneForDisplay(findDispatchPhone(phones, 'DispatchCell')?.phone),
        { shouldDirty: true }
      )
    }
    if (!current.dispatchAfterHours?.trim()) {
      setValue(
        'dispatchAfterHours',
        formatPhoneForDisplay(
          findDispatchPhone(phones, 'DispatchAfterHoursPhone')?.phone
        ),
        { shouldDirty: true }
      )
    }
  }, [company, companyId, emails, getValues, phones, setValue])

  useEffect(() => {
    autofillKeyRef.current = null
  }, [companyId])

  return null
}

const LoadCarrierForm = ({
  carrierSuggestions,
  carrierSuggestionsLoading = false,
  skipAutoFill = false,
  compact = false,
}: LoadCarrierFormProps) => {
  const { control, setValue, watch } = useFormContext()
  const selectedCarrierCompanyId = watch('selectedCarrierCompanyId')

  const applyCarrier = (carrier: CarrierCompany | CarrierSuggestion) => {
    setValue('carrierName', carrier.companyName, { shouldDirty: true })
    setValue('selectedCarrierCompanyId', carrier.companyId, { shouldDirty: true })
    if ('dispatchName' in carrier) {
      setValue('dispatchName', carrier.dispatchName ?? '', { shouldDirty: true })
    }
  }

  const clearCarrierAutoFields = () => {
    setValue('selectedCarrierCompanyId', '', { shouldDirty: true })
    setValue('carrierAddress', '', { shouldDirty: true })
    setValue('dispatchName', '', { shouldDirty: true })
    setValue('dispatchEmail', '', { shouldDirty: true })
    setValue('dispatchPhone', '', { shouldDirty: true })
    setValue('dispatchCell', '', { shouldDirty: true })
    setValue('dispatchAfterHours', '', { shouldDirty: true })
  }

  return (
    <LoadConfirmFormContainer container spacing={2} $compact={compact}>
      {selectedCarrierCompanyId && !skipAutoFill ? (
        <CarrierAutoFill companyId={selectedCarrierCompanyId} />
      ) : null}
      <FormSingleColumnLayout>
        <Stack spacing={1}>
          <InputLabel sx={{ color: 'error.main', fontWeight: 700 }}>
            Carrier Name
          </InputLabel>
          <Controller
            name="carrierName"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Autocomplete
                freeSolo
                options={carrierSuggestions}
                loading={carrierSuggestionsLoading}
                value={null}
                inputValue={value || ''}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.companyName
                }
                isOptionEqualToValue={(option, val) =>
                  typeof option !== 'string' && option.companyId === val?.companyId
                }
                onInputChange={(_event, newInputValue, reason) => {
                  if (reason === 'input') {
                    onChange(newInputValue)
                    clearCarrierAutoFields()
                  }
                }}
                onChange={(_event, newValue) => {
                  if (!newValue || typeof newValue === 'string') {
                    onChange(typeof newValue === 'string' ? newValue : '')
                    return
                  }
                  applyCarrier(newValue)
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Answer"
                    error={!!error}
                    helperText={error?.message}
                  />
                )}
              />
            )}
          />
        </Stack>
        <TextInputField label="Address" name="carrierAddress" />
        <TextInputField label="Dispatch Name" name="dispatchName" />
        <TextInputField label="Dispatch Email" name="dispatchEmail" />
        <TextInputField label="Dispatch Phone" name="dispatchPhone" />
        <TextInputField label="Dispatch Cell" name="dispatchCell" />
        <TextInputField label="Dispatch After Hours" name="dispatchAfterHours" />
      </FormSingleColumnLayout>
    </LoadConfirmFormContainer>
  )
}

export default LoadCarrierForm
